import { normalize } from "normalizr";
import { userSchema } from "../../schemas";
import * as apiHelper from "../../helpers/api";
import * as userActionTypes from "./types";
import { requestWithoutAuth } from "../../utils/request";
import { errorActionFactory } from "../../helpers/errors";

const API_URL = apiHelper.getApiUrl();

function resetAuthToken(err) {
    return {
        type: userActionTypes.RESET_AUTH,
        err
    };
}

function requestUserAuthorization(email) {
    return {
        type: userActionTypes.REQUEST_USER_AUTH,
        email
    };
}

function requestUserAuthorizationWithToken(token) {
    return {
        type: userActionTypes.REQUEST_USER_AUTH_WITH_TOKEN,
        token
    };
}

//
// Session Cookie Version
// ----------------------
// The loadSavedAuth() action loads the session reducer's state from local storage so that they don't have to
// re-authenticate every time they refresh the page. If we deploy code changes to the session object, users may wind up
// with stale session objects in their local storage. If the old object isn't compatible with our code changes, the
// user will probably get a confusing error about undefined properties.
//
// SESSION_COOKIE_VERSION tries to work around the stale cookie issue by adding some very basic validation to the
// session object. After loadSavedAuth() parses the session object, it checks to see if the object's version property
// matches this value. If they match, it assumes the session object is compatible with the current Aurora code. If they
// don't match, it forces the user to re-authenticate.
//
const SESSION_COOKIE_VERSION = 1;

export function loadSavedAuth() {
    // We save the USER_AUTH_SUCCESS action in local storage.
    // We can restore the user session by replaying that action.
    return dispatch => {
        if (!localStorage) {
            return;
        }

        const actionJson = localStorage.getItem("AUTHED_WEVO_USER");
        if (actionJson === null) {
            return dispatch(resetAuthToken());
        }

        try {
            const action = JSON.parse(actionJson);

            // Hacky check to see if this is a valid session cookie. If we change the format of the cookie, we should
            // force users to re-auth. That's the quickest way to make sure we have the data we expect to have in the
            // session state. See comment on SESSION_COOKIE_VERSION.
            const cookieVersion = action.version;
            if (cookieVersion !== SESSION_COOKIE_VERSION) {
                return dispatch(resetAuthToken());
            }
            return dispatch(action);
        } catch (e) {
            return dispatch(resetAuthToken());
        }
    };
}

export function userAuthorizationSuccess(data) {
    const { result, entities } = normalize(data, userSchema);
    const user = entities.users[result];
    const session = {
        userId: result,
        teamId: user.teams[0],
        authToken: user.apiKey.apiKey
    };

    return {
        type: userActionTypes.USER_AUTH_SUCCESS,
        version: SESSION_COOKIE_VERSION,
        session,
        entities
    };
}

function userAuthorizationFailure(err) {
    let errorMessage = "An error occurred, please try again.";
    const { response } = err;
    if (response && response.status) {
        switch (response.status) {
            case 401:
                errorMessage =
                    "Your account has been deactivated. Please contact Wevo support";
                break;
            case 403:
                errorMessage =
                    "You are not associated with any teams. If you think this is a mistake, " +
                    "please contact your team lead.";
                break;
            case 404:
                errorMessage = "Unable to find a user with those credentials";
                break;
            default:
                // not one of our messages, so we'll let the default request handler deal with it
                throw err;
        }
    }
    return errorActionFactory(userActionTypes.USER_AUTH_ERROR, err, {
        errorMessage
    });
}

function authorizeUser(email, password) {
    return dispatch => {
        dispatch(requestUserAuthorization(email));

        // Normally we chain promises after the requestWith[out]Auth call, but this time we chain them directly to the
        // HTTP request because we want to apply our error handlers before the default ones.
        return requestWithoutAuth(dispatch, http =>
            http
                .post(`${API_URL}/users/auth`, { email, password })
                .then(
                    res => dispatch(userAuthorizationSuccess(res.data)),
                    err => dispatch(userAuthorizationFailure(err))
                )
        );
    };
}

function authorizeUserWithToken(token) {
    return dispatch => {
        dispatch(resetAuthToken());
        dispatch(requestUserAuthorizationWithToken(token));

        // Normally we chain promises after the requestWith[out]Auth call, but this time we chain them directly to the
        // HTTP request because we want to apply our error handlers before the default ones.
        return requestWithoutAuth(dispatch, http =>
            http
                .post(`${API_URL}/users/auth`, { token })
                .then(
                    res => dispatch(userAuthorizationSuccess(res.data)),
                    err => dispatch(userAuthorizationFailure(err))
                )
        );
    };
}

export { authorizeUser, authorizeUserWithToken, resetAuthToken };



// WEBPACK FOOTER //
// ./src/modules/session/actions.js